import { useCRUDModalList } from '@/shared/hooks/useCRUDModalList';
import { useServiceStatus } from '@/contexts/service-status';
import { inspectionTypeService } from '@/services/inspectionType.service';
import type { InspectionType, CreateInspectionTypeRequest, UpdateInspectionTypeRequest } from '@/services/types/user.types';

const inspectionTypeServiceAdapter = {
  getAll: () => inspectionTypeService.getAllInspectionTypes(),
  create: (data: CreateInspectionTypeRequest) => inspectionTypeService.createInspectionType(data) as Promise<InspectionType>,
  update: (id: string, data: UpdateInspectionTypeRequest) => inspectionTypeService.updateInspectionType(id, data).then(() => ({} as InspectionType)),
  delete: (id: string) => inspectionTypeService.deleteInspectionType(id),
};

export function useInspectionTypeList() {
  const { isServiceOnline } = useServiceStatus();
  const isUserServiceOnline = isServiceOnline('user');

  const {
    items: inspectionTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew,
    handleEdit,
    handleDelete,
    handleModalCancel,
    handleModalSubmit,
  } = useCRUDModalList<InspectionType, CreateInspectionTypeRequest, UpdateInspectionTypeRequest>({
    service: inspectionTypeServiceAdapter,
    entityName: 'tipo de inspeção',
    enabled: isUserServiceOnline,
    mapToFormValues: (item) => ({
      name: item.name,
      code: item.code,
      description: item.description,
      isActive: item.isActive,
      requiresInspector: item.requiresInspector ?? true,
    }),
    mapFromFormValues: (values, isEditing) => {
      const requiresInspector = typeof values.requiresInspector === 'boolean'
        ? values.requiresInspector
        : Boolean(values.requiresInspector);

      if (isEditing) {
        const isActive = typeof values.isActive === 'boolean'
          ? values.isActive
          : Boolean(values.isActive);
        return {
          name: values.name as string,
          code: values.code as string,
          description: (values.description as string) || '',
          isActive,
          requiresInspector,
        };
      }
      return {
        name: values.name as string,
        code: values.code as string,
        description: (values.description as string) || '',
        requiresInspector,
      };
    },
    messages: {
      loadError: 'Erro ao carregar tipos de inspeção',
      createSuccess: 'Tipo de inspeção criado com sucesso!',
      updateSuccess: 'Tipo de inspeção atualizado com sucesso!',
      deleteSuccess: 'Tipo de inspeção excluído com sucesso!',
      deleteError: 'Erro ao excluir tipo de inspeção',
      saveError: 'Erro ao salvar tipo de inspeção',
      deleteConfirmContent: 'Tem certeza que deseja remover este tipo de inspeção? Esta ação não pode ser desfeita.',
    },
  });

  const handleNameChange = (name: string) => {
    if (!editingItem) {
      const code = name.substring(0, 3).toUpperCase();
      form.setFieldValue('code', code);
    }
  };

  const handleNewWithDefaults = () => {
    handleNew();
    requestAnimationFrame(() => {
      form.setFieldsValue({
        requiresInspector: true,
        isActive: true
      });
    });
  };

  return {
    inspectionTypes,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    handleNew: handleNewWithDefaults,
    handleEdit,
    handleDelete,
    handleNameChange,
    handleModalCancel,
    handleModalSubmit,
  };
}
